import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLaptopCode,
  faRedo,
  faBug,
  faPuzzlePiece,
  faTachometerAlt,
  faShieldAlt,
  faSearch,
  faChartLine,
  faServer,
  faTasks,
  faCookieBite,
  faLanguage,
} from '@fortawesome/free-solid-svg-icons';

const serviceList = [
  {
    icon: faLaptopCode,
    title: 'Developing Brand-New Websites',
    description:
      'Creating custom, responsive websites from scratch to meet your unique business needs.',
  },
  {
    icon: faRedo,
    title: 'Revitalizing Existing Sites',
    description:
      'Refreshing and modernizing your current website to enhance user experience and engagement.',
  },
  {
    icon: faBug,
    title: 'Bug Fixes and Updates',
    description:
      'Identifying and resolving issues to ensure your website runs smoothly and efficiently.',
  },
  {
    icon: faPuzzlePiece,
    title: 'Custom Plugin Development',
    description:
      'Developing custom WordPress plugins to extend functionality and improve performance.',
  },
  {
    icon: faTachometerAlt,
    title: 'Performance Optimization',
    description:
      'Enhancing website speed and performance for better user experience and SEO ranking.',
  },
  {
    icon: faShieldAlt,
    title: 'GDPR Compliance',
    description:
      'Implementing data protection measures to ensure compliance with GDPR regulations.',
  },
  {
    icon: faSearch,
    title: 'SEO Audits & Optimization',
    description:
      'Analyzing and improving your website’s SEO to increase visibility and organic traffic.',
  },
  {
    icon: faChartLine,
    title: 'Advanced Tracking',
    description:
      'Setting up advanced analytics to track user behavior and website performance.',
  },
  {
    icon: faServer,
    title: 'Server-Side Tagging',
    description:
      'Transforming third-party cookies into first-party cookies through server-side tagging for enhanced data control and privacy.',
  },
  {
    icon: faTasks,
    title: 'Project Management',
    description:
      'Providing project management services to ensure timely and successful project delivery.',
  },
  {
    icon: faCookieBite,
    title: 'Cookie Compliance',
    description:
      'Ensuring your website complies with cookie laws and regulations.',
  },
  {
    icon: faLanguage,
    title: 'Translation To/From Danish',
    description:
      'Offering professional translation services between Danish and English.',
  },
];

const ServiceItem = ({ service }) => (
  <div className="group relative bg-gradient-to-br from-white to-violet-50 backdrop-blur-sm border border-violet-100 rounded-3xl p-8 h-full overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-violet-500/20 hover:-translate-y-2 hover:border-violet-300">
    {/* Animated gradient overlay on hover */}
    <div className="absolute inset-0 bg-gradient-to-br from-violet-600/0 via-violet-500/0 to-purple-600/0 group-hover:from-violet-600/5 group-hover:via-violet-500/5 group-hover:to-purple-600/5 transition-all duration-500 rounded-3xl" />

    {/* Decorative corner accent */}
    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-400/10 to-transparent rounded-bl-full transition-all duration-500 group-hover:scale-150" />

    <div className="relative z-10">
      {/* Icon with modern gradient background */}
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 text-2xl flex items-center justify-center text-white shadow-lg shadow-violet-500/50 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-violet-500/70">
        <FontAwesomeIcon icon={service.icon} />
      </div>

      <div className="mt-6">
        <h4 className="text-2xl font-bold mb-3 bg-gradient-to-r from-zinc-900 to-violet-900 bg-clip-text text-transparent transition-all duration-300">
          {service.title}
        </h4>
        <p className="text-zinc-600 leading-relaxed">
          {service.description}
        </p>
      </div>
    </div>
  </div>
);

ServiceItem.propTypes = {
  service: PropTypes.shape({
    icon: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
};

const ServicesSection = () => {
  return (
    <section className="services-section w-full px-5 py-12 md:px-10 md:py-16 lg:py-20 bg-gradient-to-b from-white via-violet-50/30 to-white text-zinc-900 relative z-[1] overflow-hidden">
      {/* Modern background accent with gradient - positioned absolutely to reach screen edge */}
      <div className="absolute top-16 right-0 bottom-16 left-1/2 lg:left-[60%] rounded-l-[40px] bg-gradient-to-br from-violet-600/10 via-purple-500/10 to-violet-600/10 w-[50vw] -z-[1]" />

      {/* Decorative blur circles */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-violet-300/20 rounded-full blur-3xl -z-[1]" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-purple-300/20 rounded-full blur-3xl -z-[1]" />

      <div className="container mx-auto max-w-7xl">

        <div className="w-full max-w-2xl mb-12">
          <div className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-violet-600/10 to-purple-600/10 border border-violet-200 mb-6">
            <span className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              WHAT I DO
            </span>
          </div>
          <h2 className="text-5xl leading-tight md:text-6xl font-black mb-6 bg-gradient-to-r from-zinc-900 via-violet-900 to-purple-900 bg-clip-text text-transparent">
            Services I Offer
          </h2>
          <p className="text-xl text-zinc-600 leading-relaxed">
            Explore my range of services designed to bring your ideas to life. I combine
            innovation and expertise to deliver solutions that drive success.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16 max-w-7xl mx-auto">
          {serviceList.map((service, i) => (
            <div key={i}>
              <ServiceItem service={service} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;